import React, { useEffect, useMemo, useState } from "react";
import { fetchWithAuth } from "../config/api";
import { useAuth } from "../AuthContext";

function todayString() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function formatDateTime(iso) {
  // The API sends salon-local time (e.g. 2026-09-16T20:05:00+04:00), so the
  // clock face is read straight from the string. That keeps the salon's own
  // time on screen regardless of the viewing device's timezone. It previously
  // showed UTC because the API sent +00:00.
  if (!iso) return "";
  const d = iso.slice(0, 10);
  const t = iso.length > 10 ? iso.slice(11, 16) : "";
  return t ? `${d} ${t}` : d;
}

function money(value) {
  return Number(value || 0).toFixed(2);
}

function closeEnough(a, b) {
  return Math.abs(Number(a || 0) - Number(b || 0)) <= 0.01;
}

export default function IssuedBills() {
  const { logout } = useAuth();

  const [date, setDate] = useState(todayString);
  const [bills, setBills] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editServices, setEditServices] = useState([]);
  const [editRetailItems, setEditRetailItems] = useState([]);
  const [editPayments, setEditPayments] = useState([]);

  // Lines being added to an already-issued bill. They are held here until the
  // bill is saved, so the running total and the payment split stay correct
  // while they are being chosen.
  const [addServices, setAddServices] = useState([]);
  const [addItems, setAddItems] = useState([]);

  // Reference data for the pickers, loaded once.
  const [servicesByCatalog, setServicesByCatalog] = useState({});
  const [staffList, setStaffList] = useState([]);
  const [itemList, setItemList] = useState([]);

  const [pickService, setPickService] = useState("");
  const [pickServiceStaff, setPickServiceStaff] = useState("");
  const [pickServicePrice, setPickServicePrice] = useState("");
  const [pickItem, setPickItem] = useState("");
  const [pickItemStaff, setPickItemStaff] = useState("");
  const [pickItemQty, setPickItemQty] = useState("1");
  const [removedServiceIds, setRemovedServiceIds] = useState([]);
  const [removedRetailItemIds, setRemovedRetailItemIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const totals = useMemo(() => {
    return bills.reduce(
      (acc, bill) => {
        const amount = Number(bill.total_price || 0);
        acc.overall += amount;
        const payments = Array.isArray(bill.payments) ? bill.payments : [];
        if (payments.length) {
          payments.forEach((payment) => {
            const method = (payment.payment_method || "").toLowerCase();
            if (method === "cash") acc.cash += Number(payment.amount || 0);
            if (method === "card") acc.card += Number(payment.amount || 0);
            if (method === "online") acc.online += Number(payment.amount || 0);
          });
        } else {
          const method = (bill.payment_method || "").toLowerCase();
          if (method === "cash") acc.cash += amount;
          if (method === "card") acc.card += amount;
          if (method === "online") acc.online += amount;
        }
        return acc;
      },
      { cash: 0, card: 0, online: 0, overall: 0 }
    );
  }, [bills]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [svc, stf, itm] = await Promise.all([
          fetchWithAuth("/api/services/"),
          fetchWithAuth("/api/staff/"),
          fetchWithAuth("/api/items/"),
        ]);
        const svcJson = await svc.json().catch(() => ({}));
        const stfJson = await stf.json().catch(() => []);
        const itmJson = await itm.json().catch(() => []);
        if (cancelled) return;
        setServicesByCatalog(svcJson && typeof svcJson === "object" ? svcJson : {});
        setStaffList(Array.isArray(stfJson) ? stfJson : []);
        setItemList(Array.isArray(itmJson) ? itmJson : itmJson.results || []);
      } catch {
        /* pickers simply stay empty */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const editedBillTotal = useMemo(() => {
    const serviceTotal = editServices.reduce(
      (sum, item) => sum + Number(item.price || 0),
      0
    );
    const retailTotal = editRetailItems.reduce(
      (sum, item) => sum + Number(item.unit_price || 0) * Number(item.quantity || 1),
      0
    );
    const newServiceTotal = addServices.reduce(
      (sum, line) => sum + Number(line.price || 0),
      0
    );
    const newItemTotal = addItems.reduce(
      (sum, line) => sum + Number(line.unit_price || 0) * Number(line.quantity || 1),
      0
    );
    return serviceTotal + retailTotal + newServiceTotal + newItemTotal;
  }, [editRetailItems, editServices, addServices, addItems]);

  const editedPaymentTotal = useMemo(() => {
    return editPayments.reduce((sum, payment) => {
      if ((payment.payment_method || "").toLowerCase() === "pending") return sum;
      return sum + Number(payment.amount || 0);
    }, 0);
  }, [editPayments]);

  const editedPaidMethods = useMemo(() => {
    return [
      ...new Set(
        editPayments
          .filter((payment) => (payment.payment_method || "").toLowerCase() !== "pending")
          .filter((payment) => Number(payment.amount || 0) > 0)
          .map((payment) => (payment.payment_method || "").toLowerCase())
      ),
    ];
  }, [editPayments]);

  const isSplitPaymentEdit = editedPaidMethods.length > 1;

  const editedRemaining = Number((editedBillTotal - editedPaymentTotal).toFixed(2));

  // Payment amounts follow the services and retail items above as those lines
  // are edited: a single method takes the whole total, and a two-method split
  // keeps the first amount and moves the remainder onto the second.
  useEffect(() => {
    setEditPayments((prev) => {
      const target = Number(editedBillTotal.toFixed(2));
      const pending = (p) => (p.payment_method || "").toLowerCase() === "pending";

      if (prev.length === 1) {
        const only = prev[0];
        if (pending(only)) return prev;
        if (Number(only.amount || 0) === target) return prev;
        return [{ ...only, amount: String(target) }];
      }

      if (prev.length === 2 && !pending(prev[0]) && !pending(prev[1])) {
        const first = Number(prev[0].amount || 0);
        const remainder = Math.max(0, Number((target - first).toFixed(2)));
        if (Number(prev[1].amount || 0) === remainder) return prev;
        return [prev[0], { ...prev[1], amount: String(remainder) }];
      }

      return prev;
    });
  }, [editedBillTotal]);

  async function loadBills(selectedDate) {
    setLoading(true);
    setError("");
    try {
      const qs = selectedDate ? `?date=${selectedDate}` : "";
      const res = await fetchWithAuth(`/api/bills/${qs}`);

      if (res.status === 401 || res.status === 403) {
        logout();
        return;
      }

      if (!res.ok) {
        setError("Failed to load bills.");
        return;
      }

      const list = await res.json();
      setBills(Array.isArray(list) ? list : []);
      clearSelected();
    } catch (e) {
      console.error("Error loading bills", e);
      setError("Error loading bills.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadBills(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  function clearSelected() {
    setSelected(null);
    setEditServices([]);
    setEditRetailItems([]);
    setEditPayments([]);
    setRemovedServiceIds([]);
    setRemovedRetailItemIds([]);
  }

  function selectBill(bill) {
    setSelected(bill);
    setEditServices((bill.items || []).map((item) => ({ ...item })));
    setEditRetailItems((bill.retail_items || []).map((item) => ({ ...item })));
    setRemovedServiceIds([]);
    setRemovedRetailItemIds([]);
    const payments = bill.payments && bill.payments.length
      ? bill.payments
      : [{ payment_method: bill.payment_method || "pending", amount: bill.total_price || 0 }];
    setEditPayments(payments.map((payment) => ({ ...payment })));
    setAddServices([]);
    setAddItems([]);
    setPickService("");
    setPickServicePrice("");
    setPickItem("");
    setPickItemQty("1");
  }

  function addPendingService() {
    const found = Object.values(servicesByCatalog)
      .flat()
      .find((svc) => String(svc.id) === String(pickService));
    if (!found) return;
    setAddServices((prev) => [
      ...prev,
      {
        service_id: found.id,
        name: found.name,
        price: pickServicePrice === "" ? Number(found.price || 0) : Number(pickServicePrice),
        staff_id: pickServiceStaff || null,
      },
    ]);
    setPickService("");
    setPickServicePrice("");
  }

  function addPendingItem() {
    const found = itemList.find((it) => String(it.id) === String(pickItem));
    if (!found) return;
    setAddItems((prev) => [
      ...prev,
      {
        item_id: found.id,
        name: found.name,
        quantity: Math.max(1, Number(pickItemQty || 1)),
        unit_price: Number(found.sell_price ?? found.price ?? 0),
        staff_id: pickItemStaff || null,
      },
    ]);
    setPickItem("");
    setPickItemQty("1");
  }

  function paymentLabel(bill) {
    if (bill.payments && bill.payments.length) {
      return bill.payments
        .map((payment) => `${payment.payment_method}: ${money(payment.amount)}`)
        .join(" + ");
    }
    return bill.payment_method || "";
  }

  const isPendingMethod = (payment) =>
    (payment?.payment_method || "").toLowerCase() === "pending";

  function updatePayment(index, field, value) {
    setError("");
    setEditPayments((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };

      // With exactly two payment methods, typing an amount into one fills the
      // other with whatever is left of the bill total. Works for any pair --
      // cash + card, cash + online, card + online.
      if (copy.length === 2 && !isPendingMethod(copy[0]) && !isPendingMethod(copy[1])) {
        const other = index === 0 ? 1 : 0;
        if (field === "amount") {
          const entered = Number(value || 0);
          const remainder = Math.max(0, Number((editedBillTotal - entered).toFixed(2)));
          copy[other] = { ...copy[other], amount: String(remainder) };
        } else if (field === "payment_method") {
          // Switching method keeps the split balanced.
          const entered = Number(copy[index].amount || 0);
          const remainder = Math.max(0, Number((editedBillTotal - entered).toFixed(2)));
          copy[other] = { ...copy[other], amount: String(remainder) };
        }
      }
      return copy;
    });
  }

  function addPayment() {
    setError("");
    setEditPayments((prev) => {
      const paid = prev
        .filter((payment) => !isPendingMethod(payment))
        .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
      const remainder = Math.max(0, Number((editedBillTotal - paid).toFixed(2)));
      return [...prev, { payment_method: "cash", amount: remainder ? String(remainder) : "" }];
    });
  }

  function removePayment(index) {
    setEditPayments((prev) => prev.filter((_, i) => i !== index));
  }

  function removeServiceLine(index) {
    const item = editServices[index];
    if (item?.id) setRemovedServiceIds((ids) => [...ids, item.id]);
    setEditServices((prev) => prev.filter((_, i) => i !== index));
  }

  function removeRetailLine(index) {
    const item = editRetailItems[index];
    if (item?.id) setRemovedRetailItemIds((ids) => [...ids, item.id]);
    setEditRetailItems((prev) => prev.filter((_, i) => i !== index));
  }

  function buildPaymentsPayload() {
    const activePayments = editPayments
      .map((payment) => ({
        payment_method: (payment.payment_method || "pending").toLowerCase(),
        amount: Number(payment.amount || 0),
      }))
      .filter((payment) => payment.payment_method === "pending" || payment.amount > 0);

    const paidPayments = activePayments.filter((payment) => payment.payment_method !== "pending");
    const uniquePaidMethods = [...new Set(paidPayments.map((payment) => payment.payment_method))];

    if (paidPayments.length === 0) {
      return { ok: true, payments: [{ payment_method: "pending", amount: 0 }] };
    }

    if (uniquePaidMethods.length === 1) {
      return {
        ok: true,
        payments: [{ payment_method: uniquePaidMethods[0], amount: Number(editedBillTotal.toFixed(2)) }],
      };
    }

    const splitTotal = paidPayments.reduce((sum, payment) => sum + payment.amount, 0);
    if (!closeEnough(splitTotal, editedBillTotal)) {
      return {
        ok: false,
        error:
          `Cash + Card total must match the edited bill total. ` +
          `Bill total is AED ${money(editedBillTotal)}, but payments are AED ${money(splitTotal)}.`,
      };
    }

    return { ok: true, payments: paidPayments };
  }

  async function saveBillChanges() {
    if (!selected) return;
    const paymentBuild = buildPaymentsPayload();
    if (!paymentBuild.ok) {
      setError(paymentBuild.error);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await fetchWithAuth(`/api/bills/${selected.id}/`, {
        method: "PATCH",
        body: JSON.stringify({
          services: editServices.map((item) => ({
            id: item.id,
            price: Number(item.price || 0),
          })),
          remove_service_ids: removedServiceIds,
          retail_items: editRetailItems.map((item) => ({
            id: item.id,
            quantity: Number(item.quantity || 1),
            unit_price: Number(item.unit_price || 0),
          })),
          remove_retail_item_ids: removedRetailItemIds,
          add_services: addServices.map((line) => ({
            service_id: line.service_id,
            price: Number(line.price || 0),
            staff_id: line.staff_id || null,
          })),
          add_items: addItems.map((line) => ({
            item_id: line.item_id,
            quantity: Number(line.quantity || 1),
            unit_price: Number(line.unit_price || 0),
            staff_id: line.staff_id || null,
          })),
          payments: paymentBuild.payments,
        }),
      });
      const js = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(js.error || "Failed to update bill.");
        return;
      }
      setSelected(js);
      setEditServices((js.items || []).map((item) => ({ ...item })));
      setEditRetailItems((js.retail_items || []).map((item) => ({ ...item })));
      setEditPayments((js.payments || []).map((payment) => ({ ...payment })));
      setRemovedServiceIds([]);
      setRemovedRetailItemIds([]);
      setAddServices([]);
      setAddItems([]);
      setBills((prev) => prev.map((bill) => (bill.id === js.id ? js : bill)));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card">
      <h3>Issued Bills</h3>

      <div style={{ marginBottom: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <label className="label" style={{ marginBottom: 0 }}>Date:</label>
        <input
          type="date"
          className="input"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          style={{ maxWidth: 200 }}
        />
        <button type="button" className="btn" onClick={() => loadBills(date)} disabled={loading}>
          Refresh
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {[
          ["Cash", totals.cash],
          ["Card", totals.card],
          ["Online", totals.online],
          ["Overall", totals.overall],
        ].map(([label, value]) => (
          <div className="card" style={{ padding: 8, flex: "1 1 150px" }} key={label}>
            <div className="label" style={{ marginBottom: 4 }}>{label}</div>
            <div style={{ fontWeight: "bold" }}>{money(value)}</div>
          </div>
        ))}
      </div>

      {loading && <div>Loading bills...</div>}
      {error && <div style={{ color: "red", marginBottom: 8 }}>{error}</div>}
      {!loading && bills.length === 0 && !error && <div>No bills found for this date.</div>}

      {bills.length > 0 && (
        <div style={{ maxHeight: 280, overflow: "auto", border: "1px solid #eee", marginBottom: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: 4 }}>#</th>
                <th style={{ textAlign: "left", padding: 4 }}>Date</th>
                <th style={{ textAlign: "left", padding: 4 }}>Customer</th>
                <th style={{ textAlign: "left", padding: 4 }}>Phone</th>
                <th style={{ textAlign: "left", padding: 4 }}>Payment</th>
                <th style={{ textAlign: "right", padding: 4 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {bills.map((bill) => (
                <tr
                  key={bill.id}
                  onClick={() => selectBill(bill)}
                  style={{
                    cursor: "pointer",
                    background: selected && selected.id === bill.id ? "rgba(236,0,140,0.08)" : "transparent",
                  }}
                >
                  <td style={{ padding: 4 }}>{bill.bill_number || bill.id}</td>
                  <td style={{ padding: 4 }}>{formatDateTime(bill.date)}</td>
                  <td style={{ padding: 4 }}>{bill.customer_name}</td>
                  <td style={{ padding: 4 }}>{bill.customer_phone}</td>
                  <td style={{ padding: 4 }}>{paymentLabel(bill)}</td>
                  <td style={{ padding: 4, textAlign: "right" }}>{money(bill.total_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="card" style={{ marginTop: 8 }}>
          <h4>Bill Details - {selected.bill_number || selected.id}</h4>
          <p><b>Date:</b> {formatDateTime(selected.date)}</p>
          <p><b>Customer:</b> {selected.customer_name} ({selected.customer_phone})</p>

          <h5>Services</h5>
          {editServices.length === 0 && <div>No services found for this bill.</div>}
          {editServices.map((item, index) => (
            <div key={item.id} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
              <span style={{ flex: 1 }}>{item.service_name}</span>
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                value={item.price}
                onChange={(e) =>
                  setEditServices((prev) => {
                    const copy = [...prev];
                    copy[index] = { ...copy[index], price: e.target.value };
                    return copy;
                  })
                }
                style={{ maxWidth: 140 }}
              />
              <button className="btn" type="button" onClick={() => removeServiceLine(index)}>
                Remove
              </button>
            </div>
          ))}

          <h5>Retail Items</h5>
          {editRetailItems.length === 0 && <div>No retail items found for this bill.</div>}
          {editRetailItems.map((item, index) => (
            <div key={item.id} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
              <span style={{ flex: 1 }}>{item.item_name}</span>
              <input
                className="input"
                type="number"
                min="1"
                value={item.quantity}
                onChange={(e) =>
                  setEditRetailItems((prev) => {
                    const copy = [...prev];
                    copy[index] = { ...copy[index], quantity: e.target.value };
                    return copy;
                  })
                }
                style={{ maxWidth: 100 }}
              />
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                value={item.unit_price}
                onChange={(e) =>
                  setEditRetailItems((prev) => {
                    const copy = [...prev];
                    copy[index] = { ...copy[index], unit_price: e.target.value };
                    return copy;
                  })
                }
                style={{ maxWidth: 140 }}
              />
              <button className="btn" type="button" onClick={() => removeRetailLine(index)}>
                Remove
              </button>
            </div>
          ))}

          <h5>Add to this bill</h5>
          {selected?.backdated && (
            <div style={{ background: "#fff4e5", border: "1px solid #ffb74d", color: "#8a4b00",
                          borderRadius: 8, padding: "6px 10px", marginBottom: 8, fontSize: 13 }}>
              This bill is recorded for an earlier date, so stock will not change and no
              WhatsApp will be sent.
            </div>
          )}

          {(addServices.length > 0 || addItems.length > 0) && (
            <div style={{ marginBottom: 8 }}>
              {addServices.map((line, index) => (
                <div key={`ns-${index}`} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                  <span style={{ fontSize: 11, background: "#e8f5e9", color: "#1b5e20",
                                 borderRadius: 6, padding: "2px 6px" }}>new</span>
                  <span style={{ minWidth: 180 }}>{line.name}</span>
                  <input className="input" type="number" min="0" step="0.01" style={{ maxWidth: 110 }}
                         value={line.price}
                         onChange={(e) =>
                           setAddServices((prev) =>
                             prev.map((l, i) => (i === index ? { ...l, price: e.target.value } : l))
                           )
                         } />
                  <button className="btn" type="button"
                          onClick={() => setAddServices((prev) => prev.filter((_, i) => i !== index))}>
                    Remove
                  </button>
                </div>
              ))}
              {addItems.map((line, index) => (
                <div key={`ni-${index}`} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                  <span style={{ fontSize: 11, background: "#e8f5e9", color: "#1b5e20",
                                 borderRadius: 6, padding: "2px 6px" }}>new</span>
                  <span style={{ minWidth: 180 }}>{line.name}</span>
                  <input className="input" type="number" min="1" step="1" style={{ maxWidth: 70 }}
                         value={line.quantity}
                         onChange={(e) =>
                           setAddItems((prev) =>
                             prev.map((l, i) => (i === index ? { ...l, quantity: e.target.value } : l))
                           )
                         } />
                  <input className="input" type="number" min="0" step="0.01" style={{ maxWidth: 110 }}
                         value={line.unit_price}
                         onChange={(e) =>
                           setAddItems((prev) =>
                             prev.map((l, i) => (i === index ? { ...l, unit_price: e.target.value } : l))
                           )
                         } />
                  <button className="btn" type="button"
                          onClick={() => setAddItems((prev) => prev.filter((_, i) => i !== index))}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
            <select className="input" value={pickService}
                    onChange={(e) => {
                      setPickService(e.target.value);
                      const found = Object.values(servicesByCatalog).flat()
                        .find((svc) => String(svc.id) === String(e.target.value));
                      setPickServicePrice(found ? String(Number(found.price || 0)) : "");
                    }}>
              <option value="">Add a service...</option>
              {Object.entries(servicesByCatalog).map(([catalog, list]) => (
                <optgroup key={catalog} label={catalog}>
                  {(list || []).map((svc) => (
                    <option key={svc.id} value={svc.id}>
                      {svc.name} - AED {Number(svc.price || 0).toFixed(2)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <select className="input" value={pickServiceStaff}
                    onChange={(e) => setPickServiceStaff(e.target.value)}>
              <option value="">Staff (optional)</option>
              {staffList.map((st) => (
                <option key={st.id} value={st.id}>{st.display_name || st.username || st.name}</option>
              ))}
            </select>
            <input className="input" type="number" min="0" step="0.01" placeholder="Price"
                   style={{ maxWidth: 110 }} value={pickServicePrice}
                   onChange={(e) => setPickServicePrice(e.target.value)} />
            <button className="btn" type="button" onClick={addPendingService} disabled={!pickService}>
              Add service
            </button>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
            <select className="input" value={pickItem} onChange={(e) => setPickItem(e.target.value)}>
              <option value="">Add a retail item...</option>
              {itemList.map((it) => (
                <option key={it.id} value={it.id}>
                  {it.name} - AED {Number(it.sell_price ?? it.price ?? 0).toFixed(2)}
                  {it.quantity != null ? ` (${it.quantity} in stock)` : ""}
                </option>
              ))}
            </select>
            <select className="input" value={pickItemStaff}
                    onChange={(e) => setPickItemStaff(e.target.value)}>
              <option value="">Staff (optional)</option>
              {staffList.map((st) => (
                <option key={st.id} value={st.id}>{st.display_name || st.username || st.name}</option>
              ))}
            </select>
            <input className="input" type="number" min="1" step="1" placeholder="Qty"
                   style={{ maxWidth: 80 }} value={pickItemQty}
                   onChange={(e) => setPickItemQty(e.target.value)} />
            <button className="btn" type="button" onClick={addPendingItem} disabled={!pickItem}>
              Add item
            </button>
          </div>

          <h5>Payments</h5>
          {editPayments.map((payment, index) => (
            <div key={index} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
              <select
                className="input"
                value={payment.payment_method}
                onChange={(e) => updatePayment(index, "payment_method", e.target.value)}
              >
                <option value="pending">Pending</option>
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="online">Online</option>
              </select>
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                value={payment.amount}
                onChange={(e) => updatePayment(index, "amount", e.target.value)}
                style={{ maxWidth: 140 }}
              />
              <button className="btn" type="button" onClick={() => removePayment(index)}>
                Remove
              </button>
            </div>
          ))}
          <button className="btn" type="button" onClick={addPayment}>Add Payment</button>

          <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <strong>Edited Total: AED {money(editedBillTotal)}</strong>
            {isSplitPaymentEdit ? (
              <>
                <strong>Payment Total: AED {money(editedPaymentTotal)}</strong>
                {closeEnough(editedPaymentTotal, editedBillTotal) ? (
                  <span style={{ color: "#2e7d32", fontWeight: "bold" }}>
                    Balanced
                  </span>
                ) : (
                  <span style={{ color: "red", fontWeight: "bold" }}>
                    {editedRemaining > 0
                      ? `AED ${money(editedRemaining)} still unallocated`
                      : `AED ${money(Math.abs(editedRemaining))} over the total`}
                    {" "}- payments must add up to AED {money(editedBillTotal)}
                  </span>
                )}
              </>
            ) : (
              <span style={{ color: "#666" }}>
                Single payment method will use the edited total automatically.
              </span>
            )}
          </div>

          <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
            <button className="btn" type="button" onClick={saveBillChanges} disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </button>
            <strong>Saved Total: AED {money(selected.total_price)}</strong>
          </div>
        </div>
      )}
    </div>
  );
}
